/**
 * アクセス管理ページ（UAC-08b）
 *
 * ユーザー（鍵）ごとの tool / source アクセスを individual access-policy として
 * 付与・剥奪し（UAC-07 `/admin/workspaces/:id/access-policies`）、MCP 鍵の発行・失効
 * （UAC-07 `/admin/workspaces/:id/mcp-tokens`）を行う。
 *
 * tool の resource_id は packages/mcp-server/src/mcp-server.ts の registerTool 一覧、
 * source の resource_id は CH/MCP の source gate id と揃える。
 */

import { useEffect, useState } from 'react'
import { Check, Copy, Plus, RefreshCw, Trash2 } from 'lucide-react'
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Modal,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Toggle,
} from '@/components'
import { useResolvedWorkspace } from '@/hooks/useWorkspaces'
import {
  useAccessPolicies,
  useCreateAccessPolicy,
  useDeleteAccessPolicy,
  useCreateMcpToken,
  useDeleteMcpToken,
  useMcpTokens,
  useReissueMcpToken,
  useUserSlots,
} from '@/hooks/useAccessManagement'
import { McpKeyIssueWizard } from './McpKeyIssueWizard'
import { ApiError } from '@/lib/api'
import { SOURCE_RESOURCE_IDS } from '@/lib/source-resources'
import { runtimeConfigValue } from '@/lib/runtime-config'
import {
  ACCESS_PRESET_CLASSIFICATION_LABELS,
  buildPluginInstallCommand,
  classifyAccessPreset,
  PRESET_ALL_TOOL_IDS,
  resolveMcpServerUrl,
  SOURCE_RESOURCE_ENGINE,
  SOURCE_RESOURCE_KIND,
  TOOL_RESOURCE_ENGINE,
  TOOL_RESOURCE_KIND,
} from '@/lib/access-policy-presets'
import { isScreenEnabled } from '@/lib/nav-config'
import { useScreenSet } from '@/lib/use-screen-set'
import { WORKSPACE_ID_REGEX } from '@/types/api'
import type {
  AccessPolicy,
  HokageUserSlotOption,
  McpToken,
  UacAdminResourceEngine,
} from '@/types/api'

const WORKSPACE_ID = import.meta.env.VITE_DEFAULT_WORKSPACE_ID as string | undefined

function findPolicy(
  policies: AccessPolicy[],
  engine: UacAdminResourceEngine,
  resourceId: string,
  principalRef: string,
  effect: 'allow' | 'deny',
): AccessPolicy | undefined {
  return policies.find(
    (p) =>
      p.resourceEngine === engine &&
      p.resourceId === resourceId &&
      p.principalRef === principalRef &&
      p.effect === effect,
  )
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

function tokenStatus(token: McpToken): { label: string; tone: 'success' | 'destructive' | 'muted' } {
  if (token.revokedAt) return { label: '失効済み', tone: 'destructive' }
  if (token.expiresAt && new Date(token.expiresAt).getTime() < Date.now()) {
    return { label: '期限切れ', tone: 'muted' }
  }
  return { label: '有効', tone: 'success' }
}

/** UCX-05: userSlot → 表示名。未紐付けは「未指定」。 */
function userSlotDisplayName(
  userSlot: string | null | undefined,
  userSlots: HokageUserSlotOption[],
): string {
  if (!userSlot) return '未指定'
  return userSlots.find((o) => o.userSlot === userSlot)?.displayName ?? userSlot
}

/**
 * UCX-05: 鍵一覧の「権限プリセット」列。userSlot が無い鍵は principalRef の
 * 概念自体が無い（access-policy に紐付けようがない）ため「—」を表示する。
 */
function accessPresetLabel(token: McpToken, policies: AccessPolicy[]): string {
  if (!token.userSlot) return '—'
  return ACCESS_PRESET_CLASSIFICATION_LABELS[classifyAccessPreset(policies, token.userSlot)]
}

function ResourceToggleTable({
  engine,
  mode,
  resourceKindLabel,
  resourceIds,
  principalRef,
  policies,
  disabled,
  onToggle,
}: {
  engine: UacAdminResourceEngine
  /**
   * UAC-FIX-02: source 層は 'deny'（OFF = 明示 deny policy 作成）。
   * tool 層は従来通り 'allow'（OFF = allow 削除。mcp_tool は implicit deny なので
   * allow 削除だけで遮断が成立する）。
   */
  mode: 'allow' | 'deny'
  resourceKindLabel: string
  resourceIds: readonly string[]
  principalRef: string
  policies: AccessPolicy[]
  disabled: boolean
  onToggle: (resourceId: string, next: boolean) => void
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{resourceKindLabel}</TableHead>
          <TableHead>状態</TableHead>
          <TableHead className="text-right">許可</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {resourceIds.map((resourceId) => {
          const allow = findPolicy(policies, engine, resourceId, principalRef, 'allow')
          const deny =
            mode === 'deny' ? findPolicy(policies, engine, resourceId, principalRef, 'deny') : undefined
          const checked = allow !== undefined && deny === undefined
          return (
            <TableRow key={resourceId}>
              <TableCell className="font-medium">{resourceId}</TableCell>
              <TableCell>
                {deny ? (
                  <Badge tone="destructive">遮断</Badge>
                ) : checked ? (
                  <Badge tone="success">許可</Badge>
                ) : (
                  <Badge tone="muted">未許可</Badge>
                )}
              </TableCell>
              <TableCell className="text-right">
                <Toggle
                  checked={checked}
                  disabled={disabled}
                  onCheckedChange={(next) => onToggle(resourceId, next)}
                  id={`${engine}-${resourceId}`}
                />
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

function PluginSetupCard({ installCommand }: { installCommand: string }) {
  const [copied, setCopied] = useState(false)

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(installCommand)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Plugin セットアップ</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-sm text-muted-foreground">
          初回だけ plugin を入れてから、発行された MCP 鍵で接続します。
        </p>
        <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2">
          <code className="flex-1 overflow-x-auto text-xs">{installCommand}</code>
          <button
            onClick={() => void handleCopy()}
            className="flex items-center gap-1 rounded-md border px-2 py-1 text-xs hover:bg-muted"
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? 'コピー済み' : 'コピー'}
          </button>
        </div>
      </CardContent>
    </Card>
  )
}

export function AccessManagementPage() {
  const resolvedWorkspace = useResolvedWorkspace(WORKSPACE_ID)
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState('')

  // team_pulse は operator（橘）限定 tool（MCP 側の restrictedToolIdentities で fail-closed
  // 制限済み）。管理画面のチェックボックスも、ログイン中ユーザーが team-pulse 画面を
  // 持たない（= 対象外の管理者）場合は出さない。PRESET_ALL_TOOL_IDS 自体は
  // mcp-server.test.ts の drift test が完全一致を要求するため変更しない。
  const screenSet = useScreenSet()
  const canManageTeamPulse =
    screenSet.status === 'ready' && isScreenEnabled('team-pulse', screenSet.screens)
  const toolResourceIds = canManageTeamPulse
    ? PRESET_ALL_TOOL_IDS
    : PRESET_ALL_TOOL_IDS.filter((id) => id !== 'team_pulse')

  useEffect(() => {
    if (resolvedWorkspace.workspaceId && resolvedWorkspace.workspaceId !== selectedWorkspaceId) {
      setSelectedWorkspaceId(resolvedWorkspace.workspaceId)
    }
  }, [resolvedWorkspace.workspaceId, selectedWorkspaceId])

  const workspaceId = selectedWorkspaceId.trim()

  const [principalRef, setPrincipalRef] = useState('')
  const trimmedPrincipal = principalRef.trim()

  const policiesQuery = useAccessPolicies(workspaceId)
  const createPolicy = useCreateAccessPolicy(workspaceId)
  const deletePolicy = useDeleteAccessPolicy(workspaceId)

  const tokensQuery = useMcpTokens(workspaceId)
  const createToken = useCreateMcpToken(workspaceId)
  const deleteToken = useDeleteMcpToken(workspaceId)
  const reissueToken = useReissueMcpToken(workspaceId)
  const userSlotsQuery = useUserSlots(workspaceId)

  const [tokenName, setTokenName] = useState('')
  const [tokenFormError, setTokenFormError] = useState<string | null>(null)
  const [issuedToken, setIssuedToken] = useState<{ name: string; rawKey: string } | null>(null)
  const [copied, setCopied] = useState(false)
  const [reissueTarget, setReissueTarget] = useState<McpToken | null>(null)
  const [reissueError, setReissueError] = useState<string | null>(null)

  const policies = policiesQuery.data?.policies ?? []
  const tokens = tokensQuery.data?.mcpTokens ?? []
  const userSlots = userSlotsQuery.data?.userSlots ?? []
  const mcpUrl = resolveMcpServerUrl(
    runtimeConfigValue('VITE_MCP_SERVER_URL', import.meta.env.VITE_MCP_SERVER_URL),
  )
  const pluginInstallCommand = buildPluginInstallCommand(mcpUrl)

  const toggleError =
    createPolicy.error instanceof Error
      ? createPolicy.error.message
      : deletePolicy.error instanceof Error
        ? deletePolicy.error.message
        : null

  const handleToggle = (
    engine: UacAdminResourceEngine,
    resourceKind: string,
    resourceId: string,
    next: boolean,
  ) => {
    if (!trimmedPrincipal) return

    // UAC-FIX-02: source 層は default-allow の経路が残るため、OFF は「allow 削除」
    // でなく「明示 deny 作成」で遮断する（resolver は deny > allow 優先）。
    // ON に戻すと deny を削除し、allow が無ければ作成する。
    if (engine === SOURCE_RESOURCE_ENGINE) {
      if (next) {
        const deny = findPolicy(policies, engine, resourceId, trimmedPrincipal, 'deny')
        if (deny) {
          void deletePolicy.mutateAsync(deny.id)
        }
        const allow = findPolicy(policies, engine, resourceId, trimmedPrincipal, 'allow')
        if (!allow) {
          void createPolicy.mutateAsync({
            resourceEngine: engine,
            resourceKind,
            resourceId,
            principalRef: trimmedPrincipal,
            actions: ['view'],
            effect: 'allow',
          })
        }
        return
      }
      const deny = findPolicy(policies, engine, resourceId, trimmedPrincipal, 'deny')
      if (!deny) {
        void createPolicy.mutateAsync({
          resourceEngine: engine,
          resourceKind,
          resourceId,
          principalRef: trimmedPrincipal,
          actions: ['view'],
          effect: 'deny',
        })
      }
      return
    }

    // tool 層: mcp_tool は implicit deny のため、allow の作成 / 削除で on/off が成立する。
    if (next) {
      void createPolicy.mutateAsync({
        resourceEngine: engine,
        resourceKind,
        resourceId,
        principalRef: trimmedPrincipal,
        actions: ['view'],
        effect: 'allow',
      })
      return
    }
    const existing = findPolicy(policies, engine, resourceId, trimmedPrincipal, 'allow')
    if (existing) {
      void deletePolicy.mutateAsync(existing.id)
    }
  }

  const handleIssueToken = async () => {
    const trimmed = tokenName.trim()
    if (!trimmed) {
      setTokenFormError('鍵の名前を入力してください')
      return
    }
    setTokenFormError(null)
    try {
      const result = await createToken.mutateAsync({ name: trimmed })
      setIssuedToken({ name: result.mcpToken.name, rawKey: result.rawKey })
      setTokenName('')
      setCopied(false)
    } catch {
      setTokenFormError('鍵の発行に失敗しました')
    }
  }

  const handleCopyRawKey = async () => {
    if (!issuedToken) return
    try {
      await navigator.clipboard.writeText(issuedToken.rawKey)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  // ─── UCX-05: 再発行フロー ───
  // 確認モーダル → 同じ userSlot/policy を維持したまま新鍵発行 + 旧鍵失効。
  // rawKey の一度だけ表示は既存の issuedToken Modal をそのまま流用する。

  const handleConfirmReissue = async () => {
    if (!reissueTarget) return
    setReissueError(null)
    try {
      const result = await reissueToken.mutateAsync(reissueTarget.id)
      setIssuedToken({ name: result.mcpToken.name, rawKey: result.rawKey })
      setCopied(false)
      setReissueTarget(null)
    } catch {
      setReissueError('再発行に失敗しました')
    }
  }

  if (resolvedWorkspace.isLoading) {
    return (
      <div className="rounded-lg border bg-card p-4 text-sm text-muted-foreground">
        利用可能なワークスペースを確認しています...
      </div>
    )
  }

  if (resolvedWorkspace.error) {
    return (
      <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4 text-sm text-destructive">
        ワークスペース一覧の取得に失敗しました。ログイン状態か API 接続を確認してください。
      </div>
    )
  }

  if (resolvedWorkspace.workspaces.length === 0) {
    return (
      <div className="rounded-lg border bg-card p-4 text-sm text-muted-foreground">
        利用可能なワークスペースがありません。先にオンボーディングでワークスペースを準備してください。
      </div>
    )
  }

  // WU-02: member role（非 admin）はサーバーが 403 を返す。空一覧として
  // 誤表示せず「権限がありません」で明示する（fail-closed の UX）。
  const isForbidden = [policiesQuery.error, tokensQuery.error, userSlotsQuery.error].some(
    (err) => err instanceof ApiError && err.status === 403,
  )
  if (isForbidden) {
    return (
      <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4 text-sm text-destructive">
        このページを表示する権限がありません。アクセス管理はワークスペース管理者のみ利用できます。
      </div>
    )
  }

  if (!WORKSPACE_ID_REGEX.test(workspaceId)) {
    return (
      <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4 text-sm text-destructive">
        workspaceId の形式が不正です。英数字で始まる 2〜128 文字の値が必要です。
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">アクセス管理</h1>
        <p className="text-muted-foreground">
          ユーザー（鍵）ごとに tool / source へのアクセスを個別に許可し、MCP 鍵を発行・失効します
        </p>
      </div>

      <McpKeyIssueWizard workspaceId={workspaceId} />
      <PluginSetupCard installCommand={pluginInstallCommand} />

      <details className="rounded-lg border bg-card">
        <summary className="cursor-pointer px-6 py-4 text-sm font-semibold">
          詳細設定（principalRef を指定して tool / source を個別に許可）
        </summary>
        <div className="space-y-6 border-t p-6">
          <Card>
            <CardHeader>
              <CardTitle>対象ユーザー</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {resolvedWorkspace.workspaces.length > 1 && (
                <div className="space-y-1">
                  <label htmlFor="access-mgmt-workspace" className="text-sm font-medium">
                    ワークスペース
                  </label>
                  <select
                    id="access-mgmt-workspace"
                    value={workspaceId}
                    onChange={(e) => setSelectedWorkspaceId(e.target.value)}
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  >
                    {resolvedWorkspace.workspaces.map((workspace) => (
                      <option key={workspace.id} value={workspace.id}>
                        {workspace.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="space-y-1">
                <label htmlFor="access-mgmt-principal" className="text-sm font-medium">
                  principalRef（対象ユーザーの ID / メールアドレス）
                </label>
                <input
                  id="access-mgmt-principal"
                  value={principalRef}
                  onChange={(e) => setPrincipalRef(e.target.value)}
                  placeholder="例: user@example.com"
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  tool / source のトグルは、この ID を principalRef とする individual policy を作成・削除します
                </p>
              </div>
            </CardContent>
          </Card>

          {toggleError && (
            <p className="rounded-md border border-destructive/20 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              アクセス設定の更新に失敗しました: {toggleError}
            </p>
          )}

          <Card>
            <CardHeader>
              <CardTitle>ツールアクセス</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {!trimmedPrincipal ? (
                <p className="p-6 text-sm text-muted-foreground">
                  先に対象ユーザーの principalRef を入力してください
                </p>
              ) : policiesQuery.isLoading ? (
                <p className="p-6 text-sm text-muted-foreground">読み込み中です...</p>
              ) : (
                <ResourceToggleTable
                  engine={TOOL_RESOURCE_ENGINE}
                  mode="allow"
                  resourceKindLabel="ツール"
                  resourceIds={toolResourceIds}
                  principalRef={trimmedPrincipal}
                  policies={policies}
                  disabled={createPolicy.isPending || deletePolicy.isPending}
                  onToggle={(resourceId, next) =>
                    handleToggle(TOOL_RESOURCE_ENGINE, TOOL_RESOURCE_KIND, resourceId, next)
                  }
                />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>ソースアクセス</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {!trimmedPrincipal ? (
                <p className="p-6 text-sm text-muted-foreground">
                  先に対象ユーザーの principalRef を入力してください
                </p>
              ) : policiesQuery.isLoading ? (
                <p className="p-6 text-sm text-muted-foreground">読み込み中です...</p>
              ) : (
                <ResourceToggleTable
                  engine={SOURCE_RESOURCE_ENGINE}
                  mode="deny"
                  resourceKindLabel="ソース"
                  resourceIds={SOURCE_RESOURCE_IDS}
                  principalRef={trimmedPrincipal}
                  policies={policies}
                  disabled={createPolicy.isPending || deletePolicy.isPending}
                  onToggle={(resourceId, next) =>
                    handleToggle(SOURCE_RESOURCE_ENGINE, SOURCE_RESOURCE_KIND, resourceId, next)
                  }
                />
              )}
            </CardContent>
          </Card>
        </div>
      </details>

      <Card>
        <CardHeader>
          <CardTitle>MCP 鍵</CardTitle>
          <button
            onClick={() => void handleIssueToken()}
            disabled={createToken.isPending}
            className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            <Plus className="h-4 w-4" />
            {createToken.isPending ? '発行中...' : '鍵を発行'}
          </button>
        </CardHeader>
        <CardContent className="space-y-3">
          <input
            value={tokenName}
            onChange={(e) => setTokenName(e.target.value)}
            placeholder="鍵の名前（例: 役員 MCP 鍵）"
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
          />
          {tokenFormError && <p className="text-xs text-destructive">{tokenFormError}</p>}
        </CardContent>
        {tokensQuery.isLoading ? (
          <p className="p-6 text-sm text-muted-foreground">読み込み中です...</p>
        ) : tokens.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">発行済みの MCP 鍵はありません</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>名前</TableHead>
                <TableHead>対象者</TableHead>
                <TableHead>権限プリセット</TableHead>
                <TableHead>プレフィックス</TableHead>
                <TableHead>状態</TableHead>
                <TableHead>最終使用</TableHead>
                <TableHead>発行日</TableHead>
                <TableHead className="text-right">操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tokens.map((token) => {
                const status = tokenStatus(token)
                const isRevoked = token.revokedAt !== null
                return (
                  <TableRow key={token.id}>
                    <TableCell className="font-medium">{token.name}</TableCell>
                    <TableCell>{userSlotDisplayName(token.userSlot, userSlots)}</TableCell>
                    <TableCell>{accessPresetLabel(token, policies)}</TableCell>
                    <TableCell className="text-muted-foreground">{token.keyPrefix}...</TableCell>
                    <TableCell>
                      <Badge tone={status.tone}>{status.label}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{formatDateTime(token.lastUsedAt)}</TableCell>
                    <TableCell className="text-muted-foreground">{formatDateTime(token.createdAt)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => {
                            setReissueError(null)
                            setReissueTarget(token)
                          }}
                          disabled={reissueToken.isPending || isRevoked}
                          className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs hover:bg-muted disabled:opacity-50"
                        >
                          <RefreshCw className="h-3.5 w-3.5" />
                          再発行
                        </button>
                        <button
                          onClick={() => void deleteToken.mutateAsync(token.id)}
                          disabled={deleteToken.isPending || isRevoked}
                          className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs text-destructive hover:bg-destructive/5 disabled:opacity-50"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          失効
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      <Modal
        open={reissueTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setReissueTarget(null)
            setReissueError(null)
          }
        }}
      >
        <ModalHeader>
          <ModalTitle>MCP 鍵を再発行しますか？</ModalTitle>
          <ModalDescription>
            {reissueTarget && (
              <>
                「{reissueTarget.name}」と同じ対象者・権限設定で新しい鍵を発行します。発行後、この鍵（
                {reissueTarget.keyPrefix}...）は失効します。
              </>
            )}
          </ModalDescription>
        </ModalHeader>
        {reissueError && <p className="text-sm text-destructive">{reissueError}</p>}
        <ModalFooter>
          <button
            onClick={() => setReissueTarget(null)}
            disabled={reissueToken.isPending}
            className="rounded-md border px-4 py-2 text-sm hover:bg-muted disabled:opacity-60"
          >
            キャンセル
          </button>
          <button
            onClick={() => void handleConfirmReissue()}
            disabled={reissueToken.isPending}
            className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            {reissueToken.isPending ? '再発行中...' : '再発行する'}
          </button>
        </ModalFooter>
      </Modal>

      <Modal
        open={issuedToken !== null}
        onOpenChange={(open) => {
          if (!open) setIssuedToken(null)
        }}
      >
        <ModalHeader>
          <ModalTitle>MCP 鍵を発行しました</ModalTitle>
          <ModalDescription>
            この鍵は今だけ表示されます。閉じると二度と表示できません。安全な場所に保存してください。
          </ModalDescription>
        </ModalHeader>
        {issuedToken && (
          <div className="space-y-2">
            <p className="text-sm font-medium">{issuedToken.name}</p>
            <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2">
              <code className="flex-1 overflow-x-auto text-xs">{issuedToken.rawKey}</code>
              <button
                onClick={() => void handleCopyRawKey()}
                className="flex items-center gap-1 rounded-md border px-2 py-1 text-xs hover:bg-muted"
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? 'コピー済み' : 'コピー'}
              </button>
            </div>
          </div>
        )}
        <ModalFooter>
          <button
            onClick={() => setIssuedToken(null)}
            className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90"
          >
            保存したので閉じる
          </button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
